import React, { useCallback, useRef, useState } from "react";
import { MessageLinkSubjectTypeEnum } from "@scrile/api-provider/dist/api/MessageLinksProvider";
import { Message, InputExtendedMessageCreate } from "@scrile/api-provider/dist/api/MessagesProvider";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import useThreadPicker, { SAVED_MESSAGES_ID, ThreadItem } from "hooks/useThreadPicker";
import emitter, { EVENTS } from "lib/emitter";
import { getMessageMetadata, stripMetaPrefix } from "lib/messageMetadata";
import providers from "lib/providers";
import "./style.scss";

// A message's `link` is an internal subject reference, not a URL preview.
// Only globally-meaningful content references survive a forward; session-scoped
// links (guest_user, ai) would dangle in the target chat, so they are dropped.
const FORWARDABLE_LINK_TYPES: string[] = [MessageLinkSubjectTypeEnum.CONTENT_SET, MessageLinkSubjectTypeEnum.STORY];

interface Props {
  /**
   * Messages to forward. Pass a single-element array for the standard
   * "Forward this message" flow; pass multiple to forward a selection in
   * one go (chronological order is preserved).
   */
  messages: Message[];
  onClose: () => void;
}

function ForwardMessageModal({ messages, onClose }: Props) {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const { threads, loading } = useThreadPicker();
  const [query, setQuery] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  // (thread:message) pairs already delivered, so a retry after a partial
  // failure (e.g. one message in a bulk forward hit the attachment limit)
  // doesn't re-send the messages that already went through.
  const sentRef = useRef<Set<string>>(new Set());

  const filtered = query ? threads.filter((t) => t.name.toLowerCase().includes(query.toLowerCase())) : threads;

  const handleForward = useCallback(
    async (target: ThreadItem) => {
      if (sending || messages.length === 0 || !user) return;
      // Synthetic Saved Messages entry — no thread exists yet, so target the
      // user id; the backend creates the self-thread on first send.
      const isSyntheticSaved = target.id === SAVED_MESSAGES_ID;
      const threadId = target.id;
      setError("");
      setSending(true);
      try {
        // Forward sequentially so the receiving thread keeps the original
        // chronological order. Each forwarded message is sent as a NEW
        // message authored by the current user, with a __META__ prefix
        // that the bubble renders as "Forwarded from <name>".
        for (const message of messages) {
          // Skip anything already delivered on a previous (partially failed) attempt.
          const sentKey = `${threadId}:${message.id}`;
          if (sentRef.current.has(sentKey)) continue;

          const senderName = (message as any).senderUser?.screenName || "User";
          // Preserve CONTENT metadata of the original message (voiceMessage,
          // voiceDuration, videoMessage, videoDuration, voiceTranscription)
          // so the receiver renders a voice player / round-video player
          // instead of falling through to a generic file attachment.
          //
          // Explicitly drop CONNECTION metadata that points back into the
          // source chat (replyTo*, threadParentId) — those ids don't exist
          // in the destination chat and would render as broken reply quotes
          // / nested-thread links.
          const originalMeta = getMessageMetadata(message) || {};
          const meta = {
            ...originalMeta,
            // CONNECTION metadata — strip (source-chat-specific ids).
            replyToMessageId: undefined,
            replyToText: undefined,
            replyToSenderId: undefined,
            replyToSenderName: undefined,
            threadParentId: undefined,
            // SYSTEM / LIFECYCLE metadata — strip. Forwarding a deleted-for-all
            // message would otherwise render a "deleted" placeholder in the
            // destination; an unknown `type` could trigger system-message
            // render paths in MessageBubble. Both should not survive a forward.
            type: undefined,
            deleted: undefined,
            deletedForAll: undefined,
            // FORWARD metadata — point at the immediate previous sender.
            // Matches Telegram-style chain behaviour: a forward-of-a-forward
            // shows the last forwarder, not the original origin.
            forwardedFromId: message.senderId,
            forwardedFromName: senderName,
          };
          const cleanText = stripMetaPrefix(message.text);
          const text = `__META__:${JSON.stringify(meta)}\n${cleanText}`;

          // Re-share the original attachments. Re-sending the original file uuid
          // creates a NEW DB File row pointing at the same shared S3 object; the
          // source message is untouched. publicAccess is preserved per-file.
          //
          // Guard: if the original is paid/locked and THIS user hasn't unlocked it,
          // forward the text only — never re-share paywalled content as a free copy.
          const locked =
            message.attachmentAvailable === false ||
            (!!message.attachmentPricePackageId &&
              message.attachmentPricePackageId !== "0" &&
              !(message.attachmentPurchasers || []).includes(String(user?.id)));
          const files = locked
            ? []
            : (message.attachment || []).map((a) => ({ uuid: a.file.uuid, publicAccess: a.publicAccess }));

          const data: InputExtendedMessageCreate = isSyntheticSaved
            ? { text, recipientIds: [String(target.userId || user.id)], pricePackageId: "0" }
            : { text, threadIds: [threadId], pricePackageId: "0" };
          if (files.length) data.files = files;
          if (message.link?.subject && FORWARDABLE_LINK_TYPES.includes(String(message.link.subject.subjectType))) {
            data.linkSubject = message.link.subject;
          }

          // eslint-disable-next-line no-await-in-loop
          const result = await providers.MessagesProvider.sendExtendedMessages({ data });
          sentRef.current.add(sentKey);
          if (result?.length > 0) {
            result.forEach((msg: any) => emitter.emit(EVENTS.MESSAGES_NEW_MESSAGE, msg));
          }
        }
        onClose();
      } catch (e) {
        console.error("Failed to forward:", e);
        setError(t("Couldn't forward this message"));
      } finally {
        setSending(false);
      }
    },
    [messages, sending, onClose, user, t]
  );

  return (
    <div className="forward-modal-overlay" onClick={onClose}>
      <div className="forward-modal" onClick={(e) => e.stopPropagation()}>
        <div className="forward-modal__header">
          <h3>
            {messages.length > 1 ? t("Forward {{count}} messages", { count: messages.length }) : t("Forward message")}
          </h3>
          <button className="forward-modal__close" onClick={onClose} type="button">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
        <div className="forward-modal__search">
          <input
            type="text"
            placeholder={t("Search chats...")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="forward-modal__list">
          {error && <div className="forward-modal__empty forward-modal__error">{error}</div>}
          {loading && <div className="forward-modal__empty">{t("Loading...")}</div>}
          {!loading &&
            filtered.map((thread) => (
              <div
                key={thread.id}
                className={`forward-modal__item${sending ? " __disabled" : ""}`}
                onClick={() => !sending && handleForward(thread)}
              >
                <div className="forward-modal__item-avatar">{thread.name.charAt(0).toUpperCase()}</div>
                <div className="forward-modal__item-name">{thread.name}</div>
                <span className="forward-modal__item-type">
                  {thread.subjectType === "group" ? t("Group") : t("Chat")}
                </span>
              </div>
            ))}
          {!loading && filtered.length === 0 && <div className="forward-modal__empty">{t("No chats found")}</div>}
        </div>
      </div>
    </div>
  );
}

export default ForwardMessageModal;
